import React from 'react';
import { Paper, Typography, Box, Button } from '@mui/material';
import { Check } from '@mui/icons-material';
import { colors, radii, fonts } from '../theme/colors';

/**
 * Reusable subscription plan card. Used by SubscriptionUI (homepage pricing),
 * AuthModal (plan picker in signup flow), and BillingPortal (upgrade/downgrade).
 *
 * Emphasis is HIERARCHY, not per-tier identity: whichever plan carries a
 * `badge` becomes the featured card (2px seal border, seal CTA, seal badge
 * overlapping the top edge). The free plan gets an outline CTA; every other
 * paid tier gets a slate-teal fill. Moving the badge in src/data/plans.js moves
 * the emphasis with it — don't hardcode a color per plan id.
 *
 * @param {object}   props.plan            Plan definition (see src/data/plans.js).
 *                                         Required keys: id, name, price, period, features, cta.
 *                                         Optional: yearlyPrice, yearlyPeriod, tagline, badge.
 * @param {'monthly'|'yearly'} [props.billingPeriod='monthly']
 *                                         When 'yearly', displays plan.yearlyPrice/yearlyPeriod
 *                                         and emits a target plan id suffixed with '_yearly'.
 * @param {(planId: string) => void} [props.onSelect]
 *                                         Invoked with the resolved plan id when the CTA is clicked.
 *                                         For yearly plans this includes the '_yearly' suffix
 *                                         (e.g. 'pro_yearly'), except for the free plan.
 * @param {string|React.ReactNode} [props.ctaOverride]
 *                                         If a string, replaces the CTA button label.
 *                                         If a React element (e.g. a Chip), replaces the entire
 *                                         CTA button (used to show "Current Plan" badges).
 */
export default function PlanCard({ plan, billingPeriod = 'monthly', onSelect, ctaOverride }) {
  const featured = Boolean(plan.badge);
  const isFree = plan.id === 'free';
  const isYearly = billingPeriod === 'yearly';
  const displayPrice = (isYearly && plan.yearlyPrice) ? plan.yearlyPrice : plan.price;
  const displayPeriod = (isYearly && plan.yearlyPeriod) ? plan.yearlyPeriod : plan.period;
  const targetPlanId = (isYearly && plan.id !== 'free') ? `${plan.id}_yearly` : plan.id;

  return (
    <Paper
      key={plan.id}
      elevation={0}
      sx={{
        p: { xs: 3, md: '28px 24px' },
        borderRadius: `${radii.card}px`,
        border: featured ? `2px solid ${colors.seal}` : `1px solid ${colors.borderSubtle}`,
        bgcolor: colors.surface,
        position: 'relative',
        overflow: 'visible',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%'
      }}
    >
      {featured && (
        <Box
          component="span"
          sx={{
            position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)',
            bgcolor: colors.seal, color: colors.textInverse, px: 1.5, py: 0.6,
            borderRadius: `${radii.pill}px`, fontSize: '0.6875rem', fontWeight: 700,
            whiteSpace: 'nowrap', letterSpacing: '0.02em',
          }}
        >
          {plan.badge}
        </Box>
      )}

      <Typography
        sx={{
          fontFamily: fonts.mono, fontSize: '0.6875rem', textTransform: 'uppercase',
          letterSpacing: '0.06em', color: colors.teal700,
        }}
      >
        {plan.name}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.25, mt: 1.25 }}>
        <Typography sx={{ fontFamily: fonts.display, fontSize: '2.375rem', fontWeight: 600, lineHeight: 1.1, color: colors.ink }}>
          {displayPrice}
        </Typography>
        <Typography sx={{ fontSize: '0.84375rem', color: colors.textSecondary }}>{displayPeriod}</Typography>
      </Box>

      {plan.tagline && (
        <Typography sx={{ fontSize: '0.84375rem', color: colors.textSecondary, mt: 1 }}>{plan.tagline}</Typography>
      )}

      <Box sx={{ mt: 2.5, display: 'grid', gap: 1.25 }}>
        {plan.features.map((feature) => (
          <Box key={feature} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.1 }}>
            <Check sx={{ fontSize: 16, color: colors.seal, flexShrink: 0, mt: '2px' }} />
            <Typography sx={{ fontSize: '0.84375rem', color: colors.ink }}>{feature}</Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ mt: 'auto', pt: 3 }}>
        {typeof ctaOverride === 'string' || !ctaOverride ? (
          <Button
            fullWidth
            variant={isFree ? 'outlined' : 'contained'}
            color={featured ? 'primary' : 'secondary'}
            onClick={() => onSelect && onSelect(targetPlanId)}
            sx={{ py: 1.5 }}
          >
            {ctaOverride || plan.cta}
          </Button>
        ) : (
          // ctaOverride can be a React element (e.g., a Chip for "Current Plan")
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>{ctaOverride}</Box>
        )}
      </Box>
    </Paper>
  );
}
